import {Component, Element, Event, EventEmitter, h, Method, Prop} from '@stencil/core';
import '@hcaptcha/types';

@Component({
  tag: `vs-hcaptcha`,
  shadow: false
})
export class VsHcaptcha {
  @Element() el: HTMLVsHcaptchaElement;

  /**
   * @shortDescription Mandatory sitekey for hcaptcha
   * @controlName sitekey */
  @Prop() sitekey!: string;

  /**
   * @shortDescription size of captcha, can be normal,compact or invisible. Defaults to normal
   * @controlName size */
  @Prop() size: string;

  /**
   * @shortDescription Language code hcaptcha
   * @controlName language */
  @Prop() language: string;

  /**
   * @shortDescription Lazy load hcaptcha
   * @controlName lazy */
  @Prop() lazy: boolean = false;

  /**
   * @shortDescription Emit captcha expired event
   * @controlName captchaExpiredEvent */
  @Event({
    eventName: 'captchaExpiredEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onChallengeExpiredEventHandler: EventEmitter;

  /**
   * @shortDescription Emit captcha verified event
   * @controlName captchaVerifiedEvent */
  @Event({
    eventName: 'captchaVerifiedEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onCaptchaVerifiedEventHandler: EventEmitter;

  /**
   * @shortDescription Emit captcha timeout event
   * @controlName captchaTimeoutEvent */
  @Event({
    eventName: 'captchaTimeoutEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onCaptchaTimeoutEventHandler: EventEmitter;

  /**
   * @shortDescription Emit event when user opens the captcha challenge
   * @controlName challengeOpenEvent */
  @Event({
    eventName: 'challengeOpenEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onChallengeOpenEventHandler: EventEmitter;

  /**
   * @shortDescription Emit event when user closes the captcha challenge
   * @controlName challengeClosedEvent */
  @Event({
    eventName: 'challengeClosedEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onChallengeClosedEventHandler: EventEmitter;

  /**
   * @shortDescription Emit captcha error event
   * @controlName captchaErrorEvent */
  @Event({
    eventName: 'captchaErrorEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) onCaptchaErrorHandler: EventEmitter;

  // START GENAI@GHCOPILOT
 @Event({
      eventName: 'captchaRenderedEvent',
      composed: true,
      cancelable: true,
      bubbles: true,
    }) onCaptchaRenderedHandler: EventEmitter;
  // END GENAI@GHCOPILOT

  private captchaLoaded = false;


  private captchaId: any;

  @Method()
  async loadCaptcha() {
    if (!this.captchaLoaded) {
      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => this.renderCaptcha();
      document.body.appendChild(script);
      this.captchaLoaded = true;
    }
  }

  @Method()
  async resetCaptcha() {
    if (this.captchaId) {
      hcaptcha.reset(this.captchaId);
    }
  }

  @Method()
  async removeCaptcha() {
    if (this.captchaId) {
      hcaptcha.remove(this.captchaId);
    }
  }

  @Method()
  async executeCaptcha() {
    if (this.captchaId) {
      hcaptcha.execute();
    }
  }

  connectedCallback(){
    console.log('vs-hcaptcha connected to dom')
  }

  componentDidLoad() {
    console.log('vs-hcaptcha loaded')
    if (!this.lazy) {
      this.loadCaptcha();
    }
  }

  disconnectedCallback() {
    if (this.captchaId) {
      hcaptcha.reset(this.captchaId);
      hcaptcha.remove(this.captchaId);
    }
  }

  private renderCaptcha = () => {
    console.log('rendering hcaptcha', this.sitekey);
    const hCaptchaConfig = {
      sitekey: this.sitekey,
      callback: this.onSuccessHandler,
      'expired-callback': this.onChallengeExpiredHandler,
      'chalexpired-callback': this.onChallengeTimeoutHandler,
      'open-callback': this.onChallengeOpenHandler,
      'close-callback': this.onChallengeClosedHandler,
      'error-callback': this.onErrorHandler,
    };

    if (this.size) {
      hCaptchaConfig['size'] = this.size;
    }

    if (this.language) {
      hCaptchaConfig['hl'] = this.language;
    }
    console.log('vs-hcaptcha instance', this.el)
    this.captchaId = hcaptcha.render(this.el, hCaptchaConfig);
    this.onCaptchaRenderedHandler.emit("hcaptcha rendered");
  }

  private onSuccessHandler = (response: any) => {
    console.log('Success!', this);
    this.onCaptchaVerifiedEventHandler.emit(response);
  }

  private onChallengeTimeoutHandler = () => {
    console.log('Challenge Timeout, please answer again');
    this.onCaptchaTimeoutEventHandler.emit('Challenge Timeout, please answer again');
  }

  private onChallengeOpenHandler = () => {
    console.log('Challenge Opened');
    this.onChallengeOpenEventHandler.emit('Challenge Opened');
  }

  private onChallengeClosedHandler = () => {
    console.log('Challenge Closed');
    this.onChallengeClosedEventHandler.emit('Challenge Closed');
  }

  private onChallengeExpiredHandler = () => {
    console.log('Passcode response has expired. Please re-verify.');
    this.onChallengeExpiredEventHandler.emit('Passcode response has expired. Please re-verify.');
  }

  private onErrorHandler = (error: any) => {
    console.log('Captcha Error', error);
    this.onCaptchaErrorHandler.emit(error);
  }

  render() {
    return (
      <div class="h-captcha"></div>
    );
  }
}
